import { useCallback, useRef } from 'react';

/**
 * Generate a unique idempotency key (UUID v4 where available).
 */
export function generateIdempotencyKey(): string {
	if (
		typeof crypto !== 'undefined' &&
		typeof crypto.randomUUID === 'function'
	) {
		return crypto.randomUUID();
	}
	// Fallback for environments without crypto.randomUUID.
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

/**
 * Manages an idempotency key for a single logical submit.
 *
 * The key stays stable across retries of the same submit (so the backend can
 * recognize and de-duplicate a request whose response was lost, e.g. to a proxy
 * timeout) and is rotated only after a confirmed success, so the next distinct
 * submit gets a fresh key.
 *
 * Usage:
 *   const { getKey, rotate } = useIdempotencyKey();
 *   await mutation({ ...args, idempotencyKey: getKey() }).unwrap();
 *   rotate(); // only after success
 */
export function useIdempotencyKey() {
	const keyRef = useRef<string | null>(null);

	const getKey = useCallback((): string => {
		if (!keyRef.current) {
			keyRef.current = generateIdempotencyKey();
		}
		return keyRef.current;
	}, []);

	const rotate = useCallback((): void => {
		keyRef.current = null;
	}, []);

	return { getKey, rotate };
}
